import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MyFortAgentItem } from "@/api/types";
import { getAgentTierKey } from "@/config/common";
import { useGetUserContactProfile } from "@/hooks/api/useGetUserContactProfile";
import { useSetUserContactProfile } from "@/hooks/api/useSetUserContactProfile";
import { showError, showSuccess } from "@/lib/toast";
import { C } from "./homeTheme";
import { useTranslation } from "react-i18next";

function hasContactProfileFilled(profile: {
  userName?: string;
  contact?: string;
  contactAddress?: string;
}) {
  return Boolean(
    profile.userName?.trim() ||
    profile.contact?.trim() ||
    profile.contactAddress?.trim()
  );
}

export function DeliveryAddressDialog({
  open,
  onClose,
  agent,
}: {
  open: boolean;
  onClose: () => void;
  agent?: MyFortAgentItem | null;
}) {
  const { t } = useTranslation();
  const [recipientName, setRecipientName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [hasExisting, setHasExisting] = useState(false);

  const { data: profile, isLoading: profileLoading } =
    useGetUserContactProfile(open);
  const setProfileMutation = useSetUserContactProfile();

  useEffect(() => {
    if (!open || profileLoading || profile === undefined) return;
    setRecipientName(profile.userName ?? "");
    setPhone(profile.contact ?? "");
    setAddress(profile.contactAddress ?? "");
    setHasExisting(hasContactProfileFilled(profile));
  }, [open, profile, profileLoading]);

  const tierLabel = agent ? t(getAgentTierKey(agent.name)) : "";

  const handleSave = async () => {
    const userName = recipientName.trim();
    const contact = phone.trim();
    const contactAddress = address.trim();

    if (!userName || !contact || !contactAddress) {
      showError(t("homePage.deliveryAddressDialog.fillAllFields"));
      return;
    }

    try {
      await setProfileMutation.mutateAsync({
        userName,
        contact,
        contactAddress,
      });
      showSuccess(
        t(
          hasExisting
            ? "homePage.deliveryAddressDialog.updated"
            : "homePage.deliveryAddressDialog.saved"
        )
      );
      onClose();
    } catch (err) {
      showError(
        err instanceof Error
          ? err.message
          : t("homePage.deliveryAddressDialog.saveFailed")
      );
    }
  };

  const saving = setProfileMutation.isPending;
  const disabled = profileLoading || saving;

  const inputStyle = {
    background: C.card2,
    border: `1px solid ${C.border}`,
    color: C.text,
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="max-w-sm border-0 p-0 overflow-hidden"
        style={{ background: C.card, borderRadius: 16 }}
      >
        <div className="p-5 space-y-4">
          <DialogHeader>
            <DialogTitle
              className="text-sm font-bold text-left"
              style={{ color: C.text }}
            >
              <MapPin
                size={14}
                className="inline mr-1.5 align-text-bottom"
                style={{ color: C.primary }}
              />
              {agent
                ? t("homePage.deliveryAddressDialog.titleWithAgent", {
                    name: agent.name,
                    tier: tierLabel,
                  })
                : t("homePage.deliveryAddressDialog.title")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label
                className="text-[10px] mb-1 block"
                style={{ color: C.dim }}
              >
                {t("homePage.deliveryAddressDialog.recipientLabel")}
              </label>
              <input
                type="text"
                disabled={disabled}
                className="w-full rounded-lg px-3 py-2 text-xs outline-none disabled:opacity-50"
                style={inputStyle}
                placeholder={t(
                  "homePage.deliveryAddressDialog.recipientPlaceholder"
                )}
                value={recipientName}
                onChange={e => setRecipientName(e.target.value)}
              />
            </div>
            <div>
              <label
                className="text-[10px] mb-1 block"
                style={{ color: C.dim }}
              >
                {t("homePage.deliveryAddressDialog.phoneLabel")}
              </label>
              <input
                type="tel"
                disabled={disabled}
                className="w-full rounded-lg px-3 py-2 text-xs outline-none disabled:opacity-50"
                style={inputStyle}
                placeholder={t(
                  "homePage.deliveryAddressDialog.phonePlaceholder"
                )}
                value={phone}
                onChange={e => setPhone(e.target.value)}
              />
            </div>
            <div>
              <label
                className="text-[10px] mb-1 block"
                style={{ color: C.dim }}
              >
                {t("homePage.deliveryAddressDialog.addressLabel")}
              </label>
              <textarea
                rows={3}
                disabled={disabled}
                className="w-full rounded-lg px-3 py-2 text-xs outline-none resize-none disabled:opacity-50"
                style={inputStyle}
                placeholder={t(
                  "homePage.deliveryAddressDialog.addressPlaceholder"
                )}
                value={address}
                onChange={e => setAddress(e.target.value)}
              />
            </div>
          </div>

          <button
            type="button"
            disabled={disabled}
            className="w-full py-2.5 rounded-xl text-xs font-bold transition-all active:scale-95 disabled:opacity-50"
            style={{
              background: `${C.primary}15`,
              color: C.primary,
              border: `1px solid ${C.primary}20`,
            }}
            onClick={handleSave}
          >
            {profileLoading
              ? t("homePage.deliveryAddressDialog.loading")
              : saving
                ? t("homePage.deliveryAddressDialog.saving")
                : hasExisting
                  ? t("homePage.deliveryAddressDialog.update")
                  : t("homePage.deliveryAddressDialog.save")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
